import type { QueryClient } from '@tanstack/react-query'
import { invalidateTaskBoardQueries } from '@/pages/tasks/taskQueryInvalidation'
import type { UIMessage } from 'ai'
import { trpc } from '@/lib/trpc'

const FINDING_MUTATION_TOOLS = new Set([
  'findingsTriage',
  'findingsUpdate',
  'findingsVerify',
  'findingsClose',
  'findingsAcceptRisk',
  'findingsFalsePositive',
  'checksSaveFinding',
])

const RISK_MUTATION_TOOLS = new Set([
  'findingsTriage',
  'findingsAcceptRisk',
  'risksCreate',
  'risksUpdate',
  'risksLinkControl',
  'risksUnlinkControl',
  'risksLinkAsset',
  'risksUnlinkAsset',
  'risksLinkVendor',
  'risksUnlinkVendor',
])

const ASSET_MUTATION_TOOLS = new Set([
  'assetsCreate',
  'assetsUpdate',
  'assetsClassify',
  'assetsMarkReviewed',
  'assetsSyncNow',
])

const TEST_METADATA_MUTATION_TOOLS = new Set(['testsAssign', 'testsSetDueDate'])

const TASK_MUTATION_TOOLS = new Set([
  'taskCreate',
  'taskComment',
  'taskSuggestProcedure',
])

const CLAIM_MUTATION_TOOLS = new Set([
  'claimsRecord',
  'claimsEdit',
  'claimsDelete',
  'checksCode',
  'checksSaveFinding',
])

const CHECK_SCAN_MUTATION_TOOLS = new Set(['checksScan'])

const CHECK_CODE_MUTATION_TOOLS = new Set(['checksCode'])

const CHECK_REMEDIATION_MUTATION_TOOLS = new Set([
  'checksRemedyExecute',
  'checksRemedyUndo',
])

const EVIDENCE_ARTIFACT_MUTATION_TOOLS = new Set(['evidenceAttachArtifacts'])

const EVIDENCE_DOCUMENT_MUTATION_TOOLS = new Set(['evidenceSaveUploadedFile'])

const EVIDENCE_REVIEW_MUTATION_TOOLS = new Set([
  'evidenceReviewSubmission',
  'evidenceApproveWithStatusSync',
])

const DRIFT_ACCEPT_MUTATION_TOOLS = new Set(['driftAccept'])

const DRIFT_RESOLUTION_MUTATION_TOOLS = new Set(['driftAccept', 'driftDismiss'])

const RUNBOOK_LINK_MUTATION_TOOLS = new Set(['runbookLink'])

const RUNBOOK_DEFINITION_MUTATION_TOOLS = new Set(['runbookEdit'])

const RUNBOOK_EXECUTION_MUTATION_TOOLS = new Set([
  'runbookExecutionExecute',
  'runbookExecutionResume',
  'runbookExecutionApprove',
  'runbookExecutionReject',
])

function outputHasError(output: unknown): boolean {
  let parsed = output
  if (typeof output === 'string') {
    try {
      parsed = JSON.parse(output)
    } catch {
      return false
    }
  }
  return (
    typeof parsed === 'object' &&
    parsed !== null &&
    (('error' in parsed && Boolean(parsed.error)) ||
      ('success' in parsed && parsed.success === false))
  )
}

export function getSuccessfulToolNames(message: UIMessage): Set<string> {
  const toolNames = new Set<string>()
  for (const part of message.parts ?? []) {
    const toolPart = part as {
      type?: string
      state?: string
      output?: unknown
      toolInvocation?: {
        toolName?: string
        state?: string
        result?: unknown
      }
    }

    if (
      toolPart.type?.startsWith('tool-') &&
      toolPart.state === 'output-available' &&
      !outputHasError(toolPart.output)
    ) {
      toolNames.add(toolPart.type.slice(5))
      continue
    }

    const invocation = toolPart.toolInvocation
    if (
      toolPart.type === 'tool-invocation' &&
      invocation?.state === 'result' &&
      invocation.toolName &&
      !outputHasError(invocation.result)
    ) {
      toolNames.add(invocation.toolName)
    }
  }
  return toolNames
}

export async function invalidateQueriesForChatTools(
  queryClient: QueryClient,
  toolNames: Set<string>
): Promise<void> {
  const findingChanged = [...toolNames].some((name) =>
    FINDING_MUTATION_TOOLS.has(name)
  )
  const riskChanged = [...toolNames].some((name) =>
    RISK_MUTATION_TOOLS.has(name)
  )
  const assetChanged = [...toolNames].some((name) =>
    ASSET_MUTATION_TOOLS.has(name)
  )
  const testMetadataChanged = [...toolNames].some((name) =>
    TEST_METADATA_MUTATION_TOOLS.has(name)
  )
  const taskChanged = [...toolNames].some((name) =>
    TASK_MUTATION_TOOLS.has(name)
  )
  const claimChanged = [...toolNames].some((name) =>
    CLAIM_MUTATION_TOOLS.has(name)
  )
  const checkScanChanged = [...toolNames].some((name) =>
    CHECK_SCAN_MUTATION_TOOLS.has(name)
  )
  const checkCodeChanged = [...toolNames].some((name) =>
    CHECK_CODE_MUTATION_TOOLS.has(name)
  )
  const checkRemediationChanged = [...toolNames].some((name) =>
    CHECK_REMEDIATION_MUTATION_TOOLS.has(name)
  )
  const evidenceArtifactsChanged = [...toolNames].some((name) =>
    EVIDENCE_ARTIFACT_MUTATION_TOOLS.has(name)
  )
  const evidenceDocumentsChanged = [...toolNames].some((name) =>
    EVIDENCE_DOCUMENT_MUTATION_TOOLS.has(name)
  )
  const evidenceReviewChanged = [...toolNames].some((name) =>
    EVIDENCE_REVIEW_MUTATION_TOOLS.has(name)
  )
  const driftAccepted = [...toolNames].some((name) =>
    DRIFT_ACCEPT_MUTATION_TOOLS.has(name)
  )
  const driftResolved = [...toolNames].some((name) =>
    DRIFT_RESOLUTION_MUTATION_TOOLS.has(name)
  )
  const runbookLinkChanged = [...toolNames].some((name) =>
    RUNBOOK_LINK_MUTATION_TOOLS.has(name)
  )
  const runbookDefinitionChanged = [...toolNames].some((name) =>
    RUNBOOK_DEFINITION_MUTATION_TOOLS.has(name)
  )
  const runbookExecutionChanged = [...toolNames].some((name) =>
    RUNBOOK_EXECUTION_MUTATION_TOOLS.has(name)
  )
  if (
    !findingChanged &&
    !riskChanged &&
    !assetChanged &&
    !testMetadataChanged &&
    !taskChanged &&
    !claimChanged &&
    !checkScanChanged &&
    !checkCodeChanged &&
    !checkRemediationChanged &&
    !evidenceArtifactsChanged &&
    !evidenceDocumentsChanged &&
    !evidenceReviewChanged &&
    !driftAccepted &&
    !driftResolved &&
    !runbookLinkChanged &&
    !runbookDefinitionChanged &&
    !runbookExecutionChanged
  ) {
    return
  }

  const invalidations: Promise<void>[] = []

  if (findingChanged) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: trpc.task.board.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.task.board.infiniteQueryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.listOpenFindingsByCES.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getStatusById.queryKey(),
      })
    )
  }

  if (riskChanged) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: trpc.riskRegister.list.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.riskRegister.getById.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.riskRegister.suggestResidual.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.riskRegister.listActivity.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.riskRegister.getStats.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.riskRegister.getMatrix.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.riskRegister.getResidualMatrix.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.riskRegister.getDistribution.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.riskRegister.risksOverTime.queryKey(),
      })
    )
  }

  if (assetChanged) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: trpc.assetManagement.list.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.assetManagement.getById.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.assetManagement.getStats.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.assetManagement.getSyncStatus.queryKey(),
      })
    )
  }

  if (testMetadataChanged) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getStatusById.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getMyTasks.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.compliance.getControlsTree.queryKey(),
      })
    )
  }

  if (taskChanged) {
    invalidations.push(
      invalidateTaskBoardQueries(queryClient),
      queryClient.invalidateQueries({
        queryKey: trpc.task.list.queryKey(),
      })
    )
  }

  if (claimChanged) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: trpc.claimTriage.list.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyClaims.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyDraftClaims.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyDocContent.queryKey(),
      })
    )
  }

  if (checkScanChanged || checkRemediationChanged) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: trpc.checker.getScanFindings.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.checker.listProviderChecks.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.checker.listCheckRunHistory.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.checker.getCheckRunDetail.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.checker.getLatestCloudConnectionScans.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.checker.getLatestGitHubRepoScans.queryKey(),
      })
    )
  }

  if (checkCodeChanged) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getScanFindings.queryKey(),
      })
    )
  }

  if (checkRemediationChanged) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getStatusById.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.listApiTestRuns.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getLatestApiTestRun.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.compliance.getControlsTree.queryKey(),
      })
    )
  }

  if (evidenceArtifactsChanged) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getStatusById.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.evidenceFile.list.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getEvidenceReview.queryKey(),
      })
    )
  }

  if (evidenceDocumentsChanged) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: trpc.vaultDocument.list.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.vaultDocument.getStats.queryKey(),
      })
    )
  }

  if (evidenceReviewChanged) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getStatusById.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getEvidenceReview.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getReviewQueue.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.getMyTasks.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.compliance.getControlsTree.queryKey(),
      })
    )
  }

  if (driftAccepted) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicies.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyDocContent.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyVersions.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyClaims.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.claimTriage.list.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.claimTriage.progress.queryKey(),
      })
    )
  }

  if (driftResolved) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: trpc.complianceProgram.getPolicyHealth.queryKey(),
      })
    )
  }

  if (runbookLinkChanged) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: trpc.guidedTemplate.listAllTests.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.compliance.getControlsTree.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.runbook.getDefinition.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.runbook.listRunbooks.queryKey(),
      })
    )
  }

  if (runbookDefinitionChanged) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: trpc.runbook.getDefinition.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.runbook.validateDefinition.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.runbook.getPublishState.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.runbook.listRunbooks.queryKey(),
      })
    )
  }

  if (runbookExecutionChanged) {
    invalidations.push(
      queryClient.invalidateQueries({
        queryKey: trpc.runbook.getRun.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.runbook.listRuns.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.runbook.listWorkspaceRuns.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.inbox.list.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.inbox.badgeCount.queryKey(),
      })
    )
  }

  await Promise.all(invalidations)
}
